import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import * as authHelper from './AuthHelpers'
import {WithChildren} from '../../../../_metronic/helpers'
import { Media, Role, User } from '@apuestes/types'
import { parseRefreshTokenResponse, refreshToken } from './_requests'
import {
  registerAuthLogoutHandler,
  redirectToLogin,
  sessionInvalidateGate,
  AuthLogoutOptions,
} from '../session'

type AuthState = {
  token: string
  user: User & { photo: Media, roles: Array<Role> }
}

type AuthContextProps = {
  auth: AuthState | undefined
  saveAuth: (auth: AuthState | undefined) => void
  currentUser: User & { photo: Media, roles: Array<Role> } | undefined
  setCurrentUser: Dispatch<SetStateAction<User & { photo: Media, roles: Array<Role> } | undefined>>
  logout: (options?: AuthLogoutOptions) => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthState | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<User & { photo: Media, roles: Array<Role> } | undefined>()

  const saveAuth = useCallback((nextAuth: AuthState | undefined) => {
    setAuth(nextAuth)
    if (nextAuth) {
      authHelper.setAuth(nextAuth)
    } else {
      authHelper.removeAuth()
    }
  }, [])

  const logout = useCallback((options?: AuthLogoutOptions) => {
    if (options?.voluntary) {
      sessionInvalidateGate.beginVoluntaryLogout()
    }
    saveAuth(undefined)
    setCurrentUser(undefined)
    if (options?.redirect) {
      redirectToLogin()
    }
  }, [saveAuth])

  useEffect(() => {
    registerAuthLogoutHandler(logout)
    return () => registerAuthLogoutHandler(null)
  }, [logout])

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const { auth, logout, saveAuth, setCurrentUser } = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    let cancelled = false

    const initSession = async () => {
      if (!auth?.token) {
        logout()
        setShowSplashScreen(false)
        return
      }

      try {
        const storageUser = authHelper.getAuth()
        const res = await refreshToken()
        if (cancelled) {
          return
        }

        const newToken = parseRefreshTokenResponse(res.data)
        if (!storageUser?.user || res.status !== 200 || !newToken) {
          logout({ redirect: true })
          return
        }

        saveAuth({ ...storageUser, token: newToken })
        setCurrentUser(storageUser.user)
        sessionInvalidateGate.reset()
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          logout({ redirect: true })
        }
      } finally {
        if (!cancelled) {
          setShowSplashScreen(false)
        }
      }
    }

    initSession()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
